import { parseRef, isValidSimpleRef } from '@/lib/utils/reference-utils';

// Re-export isValidSimpleRef from shared utilities for backward compatibility
export { isValidSimpleRef };

/**
 * Finds the passage ID for a given book and chapter reference
 * @param book - The book name
 * @param ref - Reference like "8:14" or "8:14-16" or "8-9"
 * @param allPassages - Array of all passages to search through
 * @returns The passage ID if found, null otherwise
 */
export function findPassageIdForRef(book: string, ref: string, allPassages: Passage[]): string | null {
    // Parse ref like "8:14" or "8:14-16" or "8-9"
    const parts = ref.split(':');
    let targetChapter: number;
    let targetVerse: number | undefined;

    if (parts.length === 2) {
        // Format: "8:14" or "8:14-16"
        targetChapter = parseInt(parts[0], 10);
        targetVerse = parseInt(parts[1].split('-')[0], 10); // Take first verse if it's a range
    } else {
        // Format: "8" or "8-9"
        targetChapter = parseInt(ref.split('-')[0], 10);
    }

    // Find passage that contains this reference
    for (const passage of allPassages) {
        if (passage.book !== book) continue;

        const passageRef = parseRef(passage.ref);
        if (!passageRef) continue;

        // Check if target chapter/verse falls within this passage
        if (targetVerse !== undefined) {
            // Check if verse is in range
            if (targetChapter >= passageRef.chapterStart && targetChapter <= passageRef.chapterEnd) {
                if (targetChapter === passageRef.chapterStart && targetChapter === passageRef.chapterEnd) {
                    // Same chapter passage
                    if (targetVerse >= passageRef.verseStart && targetVerse <= passageRef.verseEnd) {
                        return passage.id;
                    }
                } else if (targetChapter === passageRef.chapterStart) {
                    // First chapter of multi-chapter passage
                    if (targetVerse >= passageRef.verseStart) {
                        return passage.id;
                    }
                } else if (targetChapter === passageRef.chapterEnd) {
                    // Last chapter of multi-chapter passage
                    if (targetVerse <= passageRef.verseEnd) {
                        return passage.id;
                    }
                } else {
                    // Middle chapter of multi-chapter passage
                    return passage.id;
                }
            }
        } else {
            // Just chapter reference - check if chapter is in range
            if (targetChapter >= passageRef.chapterStart && targetChapter <= passageRef.chapterEnd) {
                return passage.id;
            }
        }
    }

    return null;
}

/**
 * Parses and formats chiasm patterns into structured elements
 * @param pattern - The chiasm pattern string
 * @returns Array of chiasm elements with level, label, and content
 */
export function formatChiasmPattern(pattern: string): { level: number; label: string; content: string }[] {
    // Remove trailing reference in parentheses if present (e.g., "(1:12-2:30)")
    const cleanPattern = pattern.replace(/\s*\([0-9:,-]+\)\s*$/, '');

    // Split by " - " or " -> " to get individual elements
    const elements = cleanPattern.split(/\s*(?:->|-)\s+/).map(el => el.trim());

    return elements.map(element => {
        // Try to match format: "A: description (verse)" or "A (description)"
        // Pattern 1: "A: Receive him with joy (v29)"
        // Pattern 2: "A (confidence in flesh)"
        // Pattern 3: "A: Paul's imprisonment (1:12-26)"
        const colonMatch = element.match(/^([A-Z]'?)\s*:\s*(.+?)(\s*\([^)]+\))?$/i);
        const parenMatch = element.match(/^([A-Z]'?)\s*\((.+)\)$/i);

        let label = '';
        let content = '';

        if (colonMatch) {
            // Format: "A: description (verse)"
            label = colonMatch[1];
            content = colonMatch[2] + (colonMatch[3] || '');
        } else if (parenMatch) {
            // Format: "A (description)"
            label = parenMatch[1];
            content = parenMatch[2];
        } else {
            // If pattern doesn't match expected format, return as-is with no indentation
            return { level: 0, label: '', content: element };
        }

        // Calculate indentation level based on the label
        // A = 0, B = 1, C = 2, D = 3, etc.
        // A' = 0, B' = 1, C' = 2, D' = 3, etc.
        const baseLetter = label.replace("'", '');
        const level = baseLetter.charCodeAt(0) - 'A'.charCodeAt(0);

        return { level, label, content };
    });
}

/**
 * Parses and formats parallelism patterns into structured elements
 * Works for Antithetical, Synonymous, Synthetic, and Climactic parallelism
 * @param pattern - The parallelism pattern string
 * @param type - The type of parallelism
 * @returns Array of parallelism elements with label and content
 */
export function formatParallelismPattern(
    pattern: string,
    type: string
): { label: string; content: string; isResponse?: boolean }[] {
    // Remove trailing reference in parentheses if present
    const cleanPattern = pattern.replace(/\s*\([0-9:,-]+\)\s*$/, '');

    // Smart split by " - " that respects parentheses and doesn't split on newlines
    const elements: string[] = [];
    let current = '';
    let parenDepth = 0;
    let i = 0;

    while (i < cleanPattern.length) {
        const char = cleanPattern[i];
        const nextChar = cleanPattern[i + 1];
        const prevChar = i > 0 ? cleanPattern[i - 1] : '';

        if (char === '(') {
            parenDepth++;
            current += char;
        } else if (char === ')') {
            parenDepth--;
            current += char;
        } else if (char === '-' && parenDepth === 0) {
            // Check if this is a separator " - " (with spaces around it)
            if (prevChar === ' ' && nextChar === ' ') {
                // This is a separator, push current element and start new one
                if (current.trim()) {
                    elements.push(current.trim());
                }
                current = '';
                i += 2; // Skip the " - "
                continue;
            } else {
                // This is just a dash in the content
                current += char;
            }
        } else if (char === '\n') {
            // Treat newlines as potential separators if we're not in parentheses
            if (parenDepth === 0 && current.trim()) {
                // Check if the next non-whitespace character is a label (A:, B:, etc.)
                const remaining = cleanPattern.slice(i + 1).trimStart();
                if (/^[A-Z]'?\s*:/.test(remaining)) {
                    // This looks like a new element starting
                    elements.push(current.trim());
                    current = '';
                    i++;
                    continue;
                }
            }
            // Otherwise, replace newline with space
            current += ' ';
        } else {
            current += char;
        }
        i++;
    }

    // Push the last element
    if (current.trim()) {
        elements.push(current.trim());
    }

    return elements.map((element, index) => {
        // Try to match format: "A: description (verse)" or just "description (verse)"
        const colonMatch = element.match(/^([A-Z]'?)\s*:\s*(.+?)(\s*\([^)]+\))?$/i);

        let label = '';
        let content = '';

        if (colonMatch) {
            // Format: "A: description (verse)"
            label = colonMatch[1];
            content = colonMatch[2] + (colonMatch[3] || '');
        } else {
            // No label, just content
            label = String.fromCharCode(65 + index); // A, B, C, etc.
            content = element;
        }

        // For antithetical parallelism, mark second element as response
        const isResponse = type.toLowerCase().includes('antithetical') && index === 1;

        return { label, content, isResponse };
    });
}

/**
 * Parses and formats inclusio patterns (bookend structure)
 * @param pattern - The inclusio pattern string
 * @returns Object with opening and closing elements
 */
export function formatInclusioPattern(pattern: string): {
    opening: string;
    closing: string;
    middle?: string;
} {
    // Remove trailing reference in parentheses if present
    const cleanPattern = pattern.replace(/\s*\([0-9:,-]+\)\s*$/, '');

    // Split by " - " to get elements
    const elements = cleanPattern.split(/\s*-\s+/).map(el => el.trim());

    if (elements.length === 2) {
        return {
            opening: elements[0].replace(/^[A-Z]'?\s*:\s*/i, ''),
            closing: elements[1].replace(/^[A-Z]'?\s*:\s*/i, ''),
        };
    } else if (elements.length > 2) {
        return {
            opening: elements[0].replace(/^[A-Z]'?\s*:\s*/i, ''),
            middle: elements.slice(1, -1).join(' - '),
            closing: elements[elements.length - 1].replace(/^[A-Z]'?\s*:\s*/i, ''),
        };
    }

    // Fallback
    return {
        opening: cleanPattern,
        closing: '',
    };
}

/**
 * Parses and formats tricolon patterns (three-part structure)
 * @param pattern - The tricolon pattern string
 * @returns Array of objects with content and whether it already has numbering
 */
export function formatTricolonPattern(pattern: string): Array<{ content: string; hasNumber: boolean }> {
    // Remove trailing reference in parentheses if present
    const cleanPattern = pattern.replace(/\s*\([0-9:,-]+\)\s*$/, '');

    // Split by " - " to get elements
    const elements = cleanPattern.split(/\s*-\s+/).map(el => el.trim());

    return elements.map(el => {
        // Remove label prefixes (A:, B:, C:)
        let content = el.replace(/^[A-Z]'?\s*:\s*/i, '');

        // Check if content already starts with a number (e.g., "1. Holy" or "1) Holy")
        const hasNumber = /^\d+[\.)]\s/.test(content);

        return { content, hasNumber };
    });
}

/**
 * Parses and formats merism patterns (contrasting extremes representing totality)
 * @param pattern - The merism pattern string
 * @returns Array of merism pairs with their contrasting elements
 */
export function formatMerismPattern(pattern: string): Array<{ label: string; content: string }> {
    // Remove trailing reference in parentheses if present
    const cleanPattern = pattern.replace(/\s*\([0-9:,-]+\)\s*$/, '');

    // Split by " - " to get elements
    const elements = cleanPattern.split(/\s*-\s+/).map(el => el.trim());

    return elements.map(element => {
        // Try to match format: "A: description (verse)" or just "description"
        const colonMatch = element.match(/^([A-Z]'?)\s*:\s*(.+?)(\s*\([^)]+\))?$/i);

        if (colonMatch) {
            return {
                label: colonMatch[1],
                content: colonMatch[2] + (colonMatch[3] || '')
            };
        }

        // No label format
        return {
            label: '',
            content: element
        };
    });
}

